import { createFileRoute, Link } from "@tanstack/react-router";
import { Section, SectionHeader } from "@/components/site/Section";
import { AdvisorCTA } from "@/components/site/AdvisorCTA";
import insightHeroSrc from "@/assets/problems-we-solve.png";
const insightHero = { url: insightHeroSrc };
import boardroomAssetSrc from "@/assets/boardroom.png";
const boardroomAsset = { url: boardroomAssetSrc };
import { CATALOG_PDF_URL, CATALOG_PDF_FILENAME } from "@/content/courses";
import { SITE_URL } from "@/lib/seo";

export const Route = createFileRoute("/insight")({
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/insight/` }],
    meta: [
      { title: "Insights | Leadership Edge Live" },
      {
        name: "description",
        content:
          "Five things you can use today: the Leadership Gaps Assessment, the 360° Leader Behavior Check, the course catalog PDF, the full course list, and the session schedule.",
      },
      { property: "og:title", content: "Insights | Leadership Edge Live" },
      {
        property: "og:description",
        content: "Start with what's already here. No forms, no gates.",
      },
    ],
  }),
  component: Resources,
});

const available = [
  {
    title: "Leadership Gaps Assessment",
    free: true,
    body: "About five minutes. Shows which of the 16 leader behaviors your managers are missing.",
    cta: "Take the assessment",
    to: "/leadership-gaps-assessment/",
  },
  {
    title: "360° Leader Behavior Check",
    free: true,
    body: "For one leader, not the whole org. They rate themselves on the same 16 behaviors while their direct reports, peers, and manager rate them too. Direct report and peer answers stay anonymous.",
    cta: "Start a 360° check",
    to: "/360-leader-check",
  },
  {
    title: "Expectation Builder",
    free: true,
    body: "A free tool for writing clear expectations your managers can hand straight to their team.",
    cta: "Open Expectation Builder",
    href: "https://free.expectation.builder.leadershipedge.live/",
  },
  {
    title: "Course catalog (PDF)",
    body: "Every course in one file. Easy to forward to whoever approves the budget.",
    cta: "Download the PDF",
    href: CATALOG_PDF_URL,
  },
  {
    title: "Browse every course",
    body: "Filter every live course by dimension, behavior, or the problem you're trying to fix.",
    cta: "Browse courses",
    to: "/course-catalog/",
  },
  {
    title: "Upcoming schedule",
    body: "Dates and times for every open session. Register your team in a few clicks.",
    cta: "See the schedule",
    to: "/schedule/",
  },
];

function Resources() {
  return (
    <>
      <section className="relative isolate overflow-hidden">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <img
            src={insightHero.url}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 50%, rgba(255,255,255,0) 100%)",
            }}
          />
        </div>
        <div className="relative mx-auto flex min-h-[620px] max-w-6xl items-center px-6 py-16 md:py-20">
          <div className="max-w-[600px]">
            <h1
              className="font-sans text-4xl font-bold leading-[1.05] tracking-tight text-[#111111] sm:text-5xl lg:text-6xl"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Start with what's already here.
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">
              Free tools and resources to help you find the right starting point.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/course-catalog/"
                className="inline-flex items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#693D94]/90"
              >
                View the Course Catalog
              </Link>
              <Link
                to="/contact/"
                className="inline-flex items-center justify-center rounded-[6px] border border-black/40 bg-transparent px-6 py-3 text-sm font-semibold text-black transition-colors hover:border-black/60 hover:bg-black/5"
              >
                Talk to a Development Pro
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Section tone="default">
        <SectionHeader
          title="Insights"
          titleClassName="text-3xl font-semibold leading-tight text-[#693D94] md:text-4xl"
          titleStyle={{ fontFamily: "'Poppins', sans-serif" }}
        />
        <div className="grid gap-6 md:grid-cols-2">
          {available.map((r) => (
            <article
              key={r.title}
              className="group flex flex-col rounded-2xl border border-white/15 bg-[#693D94] p-7 transition-all hover:-translate-y-1 hover:border-white/30 hover:shadow-[0_14px_34px_-10px_rgba(105,61,148,0.55)]"
            >
              {"free" in r && r.free && (
                <span className="mb-3 inline-flex w-fit rounded-full bg-white px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#693D94]">
                  Free tool
                </span>
              )}
              <h3 className="font-display text-lg font-bold leading-snug text-white">{r.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/80">{r.body}</p>
              {r.to ? (
                <Link
                  to={r.to}
                  className="mt-5 inline-flex w-fit items-center text-sm font-semibold text-white hover:underline"
                >
                  {r.cta}
                </Link>
              ) : (
                <a
                  href={r.href}
                  download={r.href === CATALOG_PDF_URL ? CATALOG_PDF_FILENAME : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex w-fit items-center text-sm font-semibold text-white hover:underline"
                >
                  {r.cta}
                </a>
              )}
            </article>
          ))}
        </div>
      </Section>

      <Section tone="lavender">
        <SectionHeader
          title="Case studies"
          intro="See how a Fortune 500 supply chain team and a mining safety specialist built stronger leaders with us."
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
          titleStyle={{ fontFamily: "'Poppins', sans-serif" }}
        />
        <Link
          to="/case-studies/"
          className="inline-flex items-center justify-center rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#693D94]/90"
        >
          Read the case studies
        </Link>
      </Section>

      <AdvisorCTA
        title="Not sure where to start?"
        body="Tell us what's going on with your managers. We'll point you to the right course."
        backgroundImage={boardroomAsset.url}
      />
    </>
  );
}
